import { Box, ButtonBase, Chip, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useTranslation } from "react-i18next";
import { getAlertCategory, getConfidencePercent } from "./alertPresentation";
import { useAlertAccent } from "../../context/AlertVisualContext";
import { getAlertOwnerEmail, resolveAlertMedia, useAlertVideoToken } from "../../utils/alertMedia";

const appearance = {
  fire: { Icon: LocalFireDepartmentOutlinedIcon },
  theft: { Icon: LockOutlinedIcon },
  fall: { Icon: AccessibilityNewOutlinedIcon },
  intrusion: { Icon: BlockOutlinedIcon },
  crowd: { Icon: GroupsOutlinedIcon },
  person: { Icon: PersonOutlineIcon },
  other: { Icon: WarningAmberOutlinedIcon },
};

const AlertCard = ({ alert, statusLabel, formattedDate, selected = false, onOpen }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const category = getAlertCategory(alert.detection_type);
  const { Icon } = appearance[category] || appearance.other;
  const accent = useAlertAccent(alert.detection_type)(theme.palette.mode);
  const confidence = getConfidencePercent(alert.confidence_score);
  const detectionKey = String(alert.detection_type || "").trim().toLowerCase().replace(/_detection$/, "");
  const labelKey = detectionKey === "smoke" ? "smoke" : detectionKey === "queue" ? "queue" : category;
  const customTitle = alert.title && String(alert.title).trim().toLowerCase() !== String(alert.detection_type || "").trim().toLowerCase();
  const title = customTitle ? alert.title : (category === "other"
    ? String(alert.detection_type || t("platform.common.alert")).replace(/_/g, " ")
    : t(`platform.alertCards.types.${labelKey}`, { defaultValue: t(`alertVisuals.types.${category}`) }));
  const camera = alert.camera_display_name || alert.camera_name || t("platform.common.camera");
  const location = [camera, alert.zone_name, alert.branch_name || t("platform.common.store")].filter(Boolean).join(" · ");
  const hasClip = Boolean(alert.clip_url || alert.public_url || alert.storage_key);
  const videoToken = useAlertVideoToken();
  const { thumbnailUrl } = videoToken === undefined ? {} : resolveAlertMedia(alert, getAlertOwnerEmail(), videoToken);

  return (
    <ButtonBase
      component="button"
      type="button"
      data-alert-id={alert.id}
      aria-label={`${t("platform.alertCards.viewDetail")}: ${title}`}
      aria-haspopup="dialog"
      onClick={() => onOpen(alert)}
      sx={{
        width: "100%", minWidth: 0, textAlign: "left", justifyContent: "flex-start",
        border: "1px solid", borderColor: selected ? "primary.main" : "divider",
        borderLeft: `3px solid ${accent}`, borderRadius: "8px", px: 2.5, py: 1.75,
        bgcolor: selected ? "action.selected" : "background.paper",
        color: "text.primary", transition: "background-color 150ms ease, border-color 150ms ease",
        "&:hover": { bgcolor: "action.hover", borderColor: alpha(accent, 0.5), borderLeftColor: accent },
        "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 3 },
      }}
    >
      <Box component="span" sx={{ display: "grid", placeItems: "center", width: 40, height: 40, flexShrink: 0, alignSelf: "flex-start", mr: 2, borderRadius: "8px", color: accent, bgcolor: alpha(accent, 0.1), border: `1px solid ${alpha(accent, 0.3)}` }}>
        <Icon sx={{ fontSize: 22 }} />
      </Box>
      <Box component="span" sx={{ display: "flex", minWidth: 0, flex: 1, alignItems: { xs: "flex-start", lg: "center" }, gap: 1.5, flexDirection: { xs: "column", lg: "row" } }}>
        <Box component="span" sx={{ flex: 1, minWidth: 0, width: { xs: "100%", lg: "auto" } }}>
          <Stack component="span" direction="row" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
            <Tooltip title={title}><Typography component="span" sx={{ fontSize: 14, fontWeight: 700, overflowWrap: "anywhere" }}>{title}</Typography></Tooltip>
            {alert.severity && <Chip component="span" label={t(`platform.alertCards.severity.${alert.severity}`, { defaultValue: alert.severity })} sx={{ height: 21, borderRadius: "4px", color: accent, bgcolor: alpha(accent, 0.12), border: `1px solid ${alpha(accent, 0.3)}`, "& .MuiChip-label": { fontSize: 10, px: 0.75, fontWeight: 600 } }} />}
            {statusLabel && <Typography component="span" variant="caption" color="text.secondary">{statusLabel}</Typography>}
            {!alert.viewed_by_me_at && <Tooltip title={t("platform.alerts.notViewed")}><Box component="span" aria-label={t("platform.alerts.notViewed")} sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: accent }} /></Tooltip>}
          </Stack>
          <Stack component="span" direction="row" alignItems="center" gap={1}>
            <Tooltip title={location}><Typography component="span" variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>{location}</Typography></Tooltip>
            {hasClip && <Tooltip title={t("platform.evidence.clip")}><VideocamOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} /></Tooltip>}
          </Stack>
        </Box>
        <Stack component="span" direction="row" gap={1.5} alignItems="center" flexWrap="wrap" sx={{ flexShrink: 0, justifyContent: { xs: "flex-start", lg: "flex-end" }, maxWidth: "100%" }}>
          {thumbnailUrl && <Box component="span" sx={{ width: 88, height: 58, borderRadius: 1, overflow: "hidden", bgcolor: "#000", position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Box component="img" src={thumbnailUrl} alt={t("platform.alertCards.evidencePhoto")} loading="lazy" onError={(event) => { event.currentTarget.style.display = "none"; }} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
            {hasClip && <PlayCircleOutlineIcon sx={{ position: "absolute", color: "#fff", filter: "drop-shadow(0 1px 3px #000)", pointerEvents: "none" }} />}
          </Box>}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: theme.custom.monoFont }}>{formattedDate}</Typography>
          {confidence !== null && <Tooltip title={t("platform.common.confidence", { value: confidence })}><Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 99, color: accent, bgcolor: alpha(accent, 0.08), border: `1px solid ${alpha(accent, 0.3)}`, fontFamily: theme.custom.monoFont, fontSize: 12 }}>{confidence}%</Box></Tooltip>}
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "text.secondary", fontSize: 12 }}>{t("platform.alertCards.viewDetail")}<ChevronRightIcon sx={{ fontSize: 19 }} /></Box>
        </Stack>
      </Box>
    </ButtonBase>
  );
};

export default AlertCard;
